import type { DSDReportListItemResponse } from "@/apis/dsd-editor/route";
import { ExistingReportPicker } from "@/components/common/existing-report-picker";
import { FileUploadSection } from "@/components/file-upload-section";
import { NewChip } from "@/components/common/new-chip";
import { LeftSidebar } from "@/components/app-shell";
import { Button } from "@/toggle-ui-kit/button";
import { SegmentControl } from "@/toggle-ui-kit/segment-control";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { type DsdEditorMode } from "@/types/dsd";

import { DSD_EDITOR_COPY } from "./dsd-editor-copy";

const DSD_EDITOR_CONTENT_CLASS_NAME =
  "flex w-full max-w-[1144px] flex-col gap-5";
const uploadSourceCopy = DSD_EDITOR_COPY.sources.upload;
const existingReportSourceCopy = DSD_EDITOR_COPY.sources.existingReport;

export const DsdEditorPageShell = ({ children }: { children: ReactNode }) => (
  <div className="flex h-full w-full bg-white">
    <LeftSidebar activeMenu="dsd-editor" />

    <main className="flex min-w-0 flex-1 flex-col items-center overflow-y-auto bg-white py-32">
      <div className="flex w-full min-w-[784px] flex-col items-center gap-9 p-16">
        {children}
      </div>
    </main>
  </div>
);

export const DsdEditorPageHeader = () => (
  <div className="flex w-full max-w-[1144px] flex-col items-start gap-2">
    <div className="flex items-center gap-2">
      <h1 className="title-2-bold text-label-normal">
        {DSD_EDITOR_COPY.appTitle}
      </h1>
      <NewChip color="indigo" size="large" />
    </div>
    <p className="body-1-normal-medium text-label-neutral">
      {DSD_EDITOR_COPY.page.description}
    </p>
  </div>
);

export const DsdEditorModeSwitch = ({
  value,
  onChange,
}: {
  value: DsdEditorMode;
  onChange: (mode: DsdEditorMode) => void;
}) => (
  <div className="flex w-full max-w-[1144px] justify-start">
    <SegmentControl
      items={[...DSD_EDITOR_COPY.modeItems]}
      value={value}
      onChange={onChange}
      size="small"
    />
  </div>
);

export const DsdEditorActionSection = ({
  action,
  children,
}: {
  action: ReactNode;
  children: ReactNode;
}) => (
  <section className={DSD_EDITOR_CONTENT_CLASS_NAME}>
    <div className="flex items-center justify-end">{action}</div>
    {children}
  </section>
);

export const DsdEditorUploadFileSection = ({
  isLoading,
  fileName,
  onFileSelect,
}: {
  isLoading: boolean;
  fileName?: string;
  onFileSelect: (file: File) => void;
}) => (
  <FileUploadSection
    label={uploadSourceCopy.file.label}
    accept=".dsd"
    onFileSelect={onFileSelect}
    isLoading={isLoading}
    fileName={fileName}
    description={uploadSourceCopy.file.description}
    accentColor="blue"
  />
);

export const DsdEditorExistingReportPicker = ({
  reports,
  selectedReportId,
  onSelect,
  renderReportMeta,
  isLoading,
  isError,
  onRetry,
}: {
  reports: DSDReportListItemResponse[];
  selectedReportId: string | null;
  onSelect: (
    reportId: string | null,
    report: DSDReportListItemResponse | null,
  ) => void;
  renderReportMeta: (report: DSDReportListItemResponse) => ReactNode;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) => (
  <ExistingReportPicker
    title={existingReportSourceCopy.picker.title}
    description={existingReportSourceCopy.picker.description}
    reports={reports}
    selectedReportId={selectedReportId}
    onSelect={onSelect}
    getReportId={(report) => report.reportId}
    renderReportTitle={(report) =>
      report.filename || existingReportSourceCopy.picker.missingFilename
    }
    renderReportMeta={renderReportMeta}
    isLoading={isLoading}
    isError={isError}
    emptyMessage={existingReportSourceCopy.picker.emptyMessage}
    errorMessage={existingReportSourceCopy.picker.errorMessage}
    onRetry={onRetry}
  />
);

const DsdEditorPrimaryActionButton = ({
  children,
  isPending,
  disabled,
  onClick,
}: {
  children: ReactNode;
  isPending?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <Button
    variant="solid"
    color="secondary"
    size={36}
    onClick={onClick}
    disabled={disabled || isPending}
    leftIcon={
      isPending ? <Loader2 className="size-[18px] animate-spin" /> : undefined
    }
  >
    {children}
  </Button>
);

export const DsdEditorUploadActionButton = ({
  isPending,
  disabled,
  onClick,
}: {
  isPending: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <DsdEditorPrimaryActionButton
    isPending={isPending}
    disabled={disabled}
    onClick={onClick}
  >
    {isPending
      ? DSD_EDITOR_COPY.reviewAction.pendingLabel
      : DSD_EDITOR_COPY.reviewAction.startLabel}
  </DsdEditorPrimaryActionButton>
);

export const DsdEditorExistingReportActionButton = ({
  disabled,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) => (
  <DsdEditorPrimaryActionButton disabled={disabled} onClick={onClick}>
    {DSD_EDITOR_COPY.reviewAction.startLabel}
  </DsdEditorPrimaryActionButton>
);

export const DsdEditorPagination = ({
  page,
  hasNextPage,
  disabled,
  onPrevious,
  onNext,
}: {
  page: number;
  hasNextPage: boolean;
  disabled: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) => (
  <div className="flex items-center justify-center gap-3">
    <Button
      size="small"
      variant="outlined"
      color="secondary"
      disabled={page === 0 || disabled}
      onClick={onPrevious}
    >
      {DSD_EDITOR_COPY.pagination.previous}
    </Button>
    <span className="caption-1-medium text-label-alternative">
      {DSD_EDITOR_COPY.pagination.pageLabel(page + 1)}
    </span>
    <Button
      size="small"
      variant="outlined"
      color="secondary"
      disabled={!hasNextPage || disabled}
      onClick={onNext}
    >
      {DSD_EDITOR_COPY.pagination.next}
    </Button>
  </div>
);
